import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getEntityCatalogRootPath,
  getManageRepoOwnershipPath,
  isManageRepoOwnershipPath,
  getManageJiraTeamOwnershipPath,
  isManageJiraTeamOwnershipPath,
  getManageSubscriptionOwnershipPath,
  isManageSubscriptionOwnershipPath,
  getManagePagerDutyTeamOwnershipPath,
  isManagePagerDutyTeamOwnershipPath,
} from './routing';

export interface UseManageOwnershipDialogOptions {
  onSuccessCallback?: () => void;
  dialogTarget?: ManageOwnershipDialogTarget;
  entity?: {
    kind?: string;
    spec?: {
      type?: string;
    };
  };
}

export type ManageOwnershipDialogTarget =
  | 'repo'
  | 'jira-team'
  | 'subscription'
  | 'pagerduty-team';

export interface UseManageOwnershipDialogResult {
  dialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  handleSuccess: () => void;
}

export const useManageOwnershipDialog = (
  options: UseManageOwnershipDialogOptions = {},
): UseManageOwnershipDialogResult => {
  const { onSuccessCallback, dialogTarget, entity } = options;
  const location = useLocation();
  const navigate = useNavigate();

  const inferredTarget: ManageOwnershipDialogTarget = useMemo(() => {
    if (dialogTarget) {
      return dialogTarget;
    }

    const kind = entity?.kind?.toLocaleLowerCase();
    const type = entity?.spec?.type?.toLocaleLowerCase();

    if (kind === 'component' && type === 'jira-team') {
      return 'jira-team';
    }

    if (kind === 'component' && type === 'pagerduty-team') {
      return 'pagerduty-team';
    }

    if (kind === 'resource' && type === 'subscription') {
      return 'subscription';
    }

    return 'repo';
  }, [dialogTarget, entity?.kind, entity?.spec?.type]);

  const dialogOpen = useMemo(
    () =>
      isManageRepoOwnershipPath(location.pathname) ||
      isManageJiraTeamOwnershipPath(location.pathname) ||
      isManageSubscriptionOwnershipPath(location.pathname) ||
      isManagePagerDutyTeamOwnershipPath(location.pathname),
    [location.pathname],
  );

  const getDialogPath = useCallback(
    (target: ManageOwnershipDialogTarget) => {
      if (target === 'jira-team') {
        return getManageJiraTeamOwnershipPath(location.pathname);
      }
      if (target === 'subscription') {
        return getManageSubscriptionOwnershipPath(location.pathname);
      }
      if (target === 'pagerduty-team') {
        return getManagePagerDutyTeamOwnershipPath(location.pathname);
      }
      return getManageRepoOwnershipPath(location.pathname);
    },
    [location.pathname],
  );

  const openDialog = useCallback(() => {
    const target = inferredTarget;
    const dialogPath = getDialogPath(target);

    navigate({
      pathname: dialogPath,
      search: location.search,
    });
  }, [navigate, location.search, inferredTarget, getDialogPath]);

  const closeDialog = useCallback(() => {
    navigate({
      pathname: getEntityCatalogRootPath(location.pathname),
      search: location.search,
    });
  }, [navigate, location.pathname, location.search]);

  const handleSuccess = useCallback(() => {
    if (onSuccessCallback) {
      onSuccessCallback();
    } else {
      window.location.reload();
    }
  }, [onSuccessCallback]);

  return {
    dialogOpen,
    openDialog,
    closeDialog,
    handleSuccess,
  };
};
